import React, {
  useMemo,
  useContext,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { FastField, useFormikContext } from 'formik';
import { IconButton, Tooltip } from '@mui/material';
import { Container as DraggableContainer, Draggable } from 'react-smooth-dnd';
import format from 'string-template';

import { ReactComponent as DragIcon } from '../../../../../../../../assets/icons/v2/drag.svg';
import { PrimaryButton } from '../../../../../../../../components/Button/ActionButtons';
import ConfirmDialog from '../../../../../../../components/ConfirmDialog';
import ManageProgramContext from '../../../../../../../context/ManageProgramContext';
import {
  initialProgramValues,
} from '../../validation';
import { fieldName, programAttributes } from '../../formFields';
import {
  Label,
} from '../../styles';

import { InfoTag } from '../../../../../../../../components/Tags';
import {
  Container,
  StyledFieldArray,
  ProgramListContainer,
  AddNewOptionContainer,
  StyledTypography,
  ProgramIndex,
  StyledOption,
  StyledOptionSubContent,
  StyledTextField,
  StyledTrashIcon,
  StyledAddIcon,
  StyledAutocomplete,
  DraggableContent,
  ButtonContainer,
  StyledAutocompleteWrapper,
  RepeatCountInput,
} from './styles';
import texts from './texts';

const ProgramListSelector = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [indexToRemove, setIndexToRemove] = useState(null);

  const { coachPrograms } = useContext(ManageProgramContext);
  const { values, setFieldValue } = useFormikContext();

  const programList = values[fieldName.PROGRAMS];

  useEffect(() => {
    let currentEquipmentList = programList
      .flatMap((programEntry) => (
        programEntry?.program?.equipment || []
      ));

    currentEquipmentList = Array.from(new Set(currentEquipmentList));
    setFieldValue(fieldName.EQUIPMENT, currentEquipmentList);
  }, [
    setFieldValue,
    programList,
  ]);

  const onProgramChange = useCallback((value, index) => {
    setFieldValue(`${fieldName.PROGRAMS}[${index}]`, value);
  }, [
    setFieldValue,
  ]);

  const onRepeatCountChange = useCallback((value, index) => {
    const parsed = Math.max(1, Math.trunc(Number(value) || 1));
    setFieldValue(`${fieldName.PROGRAMS}[${index}].${programAttributes.REPEAT_COUNT}`, parsed);
  }, [
    setFieldValue,
  ]);

  const onClickRemove = (index) => {
    setIndexToRemove(index);
    setShowConfirmDialog(true);
  };

  const cancelRemove = () => {
    setShowConfirmDialog(false);
    setIndexToRemove(null);
  };

  const programOptions = useMemo(() => coachPrograms.map((program) => ({
    id: program.id,
    label: program.name,
    program,
  })), [coachPrograms]);

  return (
    <Container>
      <Label>{`${texts.title}:`}</Label>
      <StyledFieldArray
        name={fieldName.PROGRAMS}
        render={({
          remove,
          push,
          move,
        }) => {
          const confirmRemove = () => {
            remove(indexToRemove);
            setShowConfirmDialog(false);
            setIndexToRemove(null);
          };

          return (
            <>
              <DraggableContainer
                dragHandleSelector=".drag-handle"
                lockAxis="y"
                onDrop={({ removedIndex, addedIndex }) => move(removedIndex, addedIndex)}
              >
                {programList.map((program, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Draggable key={index}>
                    <DraggableContent>
                      <ProgramListContainer>
                        <IconButton className="drag-handle">
                          <DragIcon />
                        </IconButton>
                        <ProgramIndex>{`${texts.program} ${index + 1}`}</ProgramIndex>
                        <FastField name={`${fieldName.PROGRAMS}.${index}`}>
                          {({ field }) => (
                            <StyledAutocompleteWrapper>
                              <StyledAutocomplete
                                {...field}
                                disablePortal
                                disableClearable
                                options={programOptions}
                                noOptionsText={texts.noOptionText}
                                onChange={(value) => onProgramChange(value, index)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                  <StyledTextField
                                    {...params}
                                    label={texts.textFieldLabel}
                                  />
                                )}
                                renderOption={(props, {
                                  id,
                                  label,
                                  program: programOption,
                                }) => (
                                  <StyledOption
                                    {...props}
                                    key={id || label}
                                    className="program-option"
                                  >
                                    {label}
                                    {programOption?.difficulty && (
                                      <StyledOptionSubContent>
                                        <InfoTag>{programOption.difficulty}</InfoTag>
                                      </StyledOptionSubContent>
                                    )}
                                  </StyledOption>
                                )}
                              />
                            </StyledAutocompleteWrapper>
                          )}
                        </FastField>
                        <Tooltip title={texts.repeatCountLabel}>
                          <RepeatCountInput
                            type="number"
                            min={1}
                            step={1}
                            value={program[programAttributes.REPEAT_COUNT] ?? 1}
                            onChange={(e) => onRepeatCountChange(e.target.value, index)}
                            onBlur={(e) => onRepeatCountChange(e.target.value, index)}
                          />
                        </Tooltip>
                        <ButtonContainer>
                          <Tooltip
                            title={format(texts.deleteConfirmMsg, {
                              programIndex: index + 1,
                            })}
                          >
                            <IconButton
                              onClick={() => onClickRemove(index)}
                            >
                              <StyledTrashIcon />
                            </IconButton>
                          </Tooltip>
                        </ButtonContainer>
                      </ProgramListContainer>
                    </DraggableContent>
                  </Draggable>
                ))}
              </DraggableContainer>
              <AddNewOptionContainer>
                <PrimaryButton
                  variant="info"
                  size="medium"
                  icon={<StyledAddIcon />}
                  onClick={() => push({ ...initialProgramValues })}
                >
                  {texts.addNewProgram}
                </PrimaryButton>
                <StyledTypography>
                  {texts.addNewProgram}
                </StyledTypography>
              </AddNewOptionContainer>
              <ConfirmDialog
                isOpen={showConfirmDialog}
                onConfirm={confirmRemove}
                onCancel={cancelRemove}
                dialogTexts={{
                  title: '',
                  content: format(texts.deleteConfirmMsg, {
                    programIndex: Number.isInteger(indexToRemove) ? indexToRemove + 1 : '',
                  }),
                }}
              />
            </>
          );
        }}
      />
    </Container>
  );
};

export default ProgramListSelector;
